// Blog API Service
import buildDetection from '../utils/buildDetection';
import { API_URL } from '../config';

const AZURE_API_URL = `${API_URL}/api`;
const LOCAL_API_URL = 'http://localhost:3001/api';

// ULTRA AGGRESSIVE pre-rendering detection - FIRST THING TO RUN
const IMMEDIATE_PRERENDER_CHECK = (() => {
    // Multiple immediate checks
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return true;
    
    const userAgent = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
    const isHeadless = userAgent.includes('HeadlessChrome') || 
                      userAgent.includes('puppeteer') || 
                      userAgent.includes('react-snap') ||
                      userAgent.includes('Chrome-Lighthouse');
    
    const hasFlags = typeof window !== 'undefined' && (
        window.__PRERENDER_INJECTED || 
        window.__REACT_SNAP__ || 
        window.__IS_BUILD_TIME__ ||
        window.__HEADLESS_CHROME__ ||
        window.__BLOG_API_DISABLED__
    );
    
    const isHeadlessWindow = typeof window !== 'undefined' && 
                            (window.outerHeight === 0 || window.outerWidth === 0);
    
    return isHeadless || hasFlags || isHeadlessWindow;
})();

// Immediate pre-rendering check - MOST AGGRESSIVE DETECTION
const IS_PRERENDERING = IMMEDIATE_PRERENDER_CHECK || 
                       typeof window === 'undefined' || 
                       typeof navigator === 'undefined' ||
                       (typeof window !== 'undefined' && window.navigator.userAgent.includes('HeadlessChrome')) ||
                       (typeof window !== 'undefined' && window.navigator.userAgent.includes('puppeteer')) ||
                       (typeof window !== 'undefined' && window.__PRERENDER_INJECTED) ||
                       (typeof window !== 'undefined' && window.__REACT_SNAP__) ||
                       (typeof process !== 'undefined' && process.env.NODE_ENV === 'production' && typeof window !== 'undefined' && !window.location.origin.includes('localhost'));

// EMERGENCY OVERRIDE: If we're in a headless environment, completely disable API calls
if (typeof window !== 'undefined' && (
    window.navigator.userAgent.includes('HeadlessChrome') ||
    window.navigator.userAgent.includes('puppeteer') ||
    window.__PRERENDER_INJECTED ||
    window.__REACT_SNAP__ ||
    window.__BLOG_API_DISABLED__
)) {
    console.log('🚨 EMERGENCY OVERRIDE: Headless environment detected - ALL API calls disabled');
    // Override fetch globally to prevent any API calls - SILENT VERSION
    // eslint-disable-next-line no-unused-vars
    const originalFetch = window.fetch;
    window.fetch = () => Promise.resolve({
        ok: false,
        status: 503,
        statusText: 'Service Unavailable - Pre-rendering',
        json: () => Promise.resolve({}),
        text: () => Promise.resolve('')
    });
}

class BlogService {
    // Try multiple API endpoints in order of preference with retry logic
    async fetchFromMultipleEndpoints(endpoint) {
        // ULTRA AGGRESSIVE pre-rendering detection - IMMEDIATE RETURN
        if (IMMEDIATE_PRERENDER_CHECK || 
            IS_PRERENDERING || 
            buildDetection.shouldSkipApiCalls() || 
            (typeof window !== 'undefined' && window.__IS_BUILD_TIME__) ||
            (typeof window !== 'undefined' && window.__BLOG_API_DISABLED__)) {
            console.log('🔄 Pre-rendering detected in blogService (ultra aggressive check), skipping API call');
            throw new Error('Pre-rendering environment - API calls disabled');
        }

        const urls = [LOCAL_API_URL, AZURE_API_URL];
        
        for (const baseUrl of urls) {
            // Retry logic for each endpoint
            for (let attempt = 1; attempt <= 3; attempt++) {
                try {
                    console.log(`Attempt ${attempt}: Trying to fetch from: ${baseUrl}${endpoint}`);
                    
                    const controller = new AbortController();
                    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
                    
                    const response = await fetch(`${baseUrl}${endpoint}`, {
                        signal: controller.signal,
                        headers: {
                            'Content-Type': 'application/json',
                            'Cache-Control': 'no-cache'
                        }
                    });
                    
                    clearTimeout(timeoutId);
                    
                    if (response.ok) {
                        const data = await response.json();
                        console.log(`✅ Successfully fetched from: ${baseUrl}${endpoint} (attempt ${attempt})`);
                        return data;
                    } else {
                        console.log(`❌ HTTP ${response.status} from: ${baseUrl}${endpoint} (attempt ${attempt})`);
                        if (attempt === 3) break; // Don't retry on last attempt
                        await new Promise(resolve => setTimeout(resolve, 1000 * attempt)); // Exponential backoff
                    }
                } catch (error) {
                    console.log(`❌ Failed to fetch from: ${baseUrl}${endpoint} (attempt ${attempt}):`, error.message);
                    if (attempt === 3) break; // Don't retry on last attempt
                    await new Promise(resolve => setTimeout(resolve, 1000 * attempt)); // Exponential backoff
                }
            }
        }
        
        throw new Error('All API endpoints failed after retries');
    }

    // Fetch all blogs from API with graceful fallback
    async fetchBlogs() {
        try {
            const data = await this.fetchFromMultipleEndpoints('/blogs');
            return data.success ? data.blogs : [];
        } catch (error) {
            console.log('📝 API not available, will use existing content');
            return [];
        }
    }

    // Fetch single blog by ID
    async fetchBlogById(id) {
        try {
            const data = await this.fetchFromMultipleEndpoints(`/blogs/${id}`);
            return data.success ? data.blog : null;
        } catch (error) {
            console.log('📝 API not available for single blog fetch');
            return null;
        }
    }

    // Transform API blog to match existing blog structure
    transformApiBlog(apiBlog) {
        return {
            id: apiBlog.id,
            slug: this.generateSlug(apiBlog.title),
            title: apiBlog.title,
            excerpt: apiBlog.short_description || apiBlog.excerpt || this.generateExcerpt(apiBlog.content), // Use short_description first
            shortDescription: apiBlog.short_description, // Map the new field directly
            author: apiBlog.author_name || "AK Consultants Team",
            authorAvatar: null,
            authorRole: apiBlog.author_department || "Technology Expert",
            date: this.formatDate(apiBlog.blog_date || apiBlog.created_at), // Use blog_date first, fallback to created_at
            category: apiBlog.category || this.categorizeContent(apiBlog.content),
            readTime: apiBlog.read_time ? `${apiBlog.read_time} min read` : this.calculateReadTime(apiBlog.content),
            image: apiBlog.image_url || "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=1200&q=80",
            images: apiBlog.images || [],
            content: apiBlog.content,
            status: apiBlog.status || 'published',
            featured: apiBlog.featured || false
        };
    }

    // Generate slug from title - improved version
    generateSlug(title) {
        if (!title) return '';
        
        return title
            .toLowerCase()
            .trim()
            .replace(/[^\w\s-]/g, '') // Remove special characters except word chars, spaces, and hyphens
            .replace(/\s+/g, '-') // Replace spaces with hyphens
            .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
            .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
    }

    // Generate excerpt from content
    generateExcerpt(content) {
        // Remove HTML tags and get first 200 characters
        const textContent = content.replace(/<[^>]*>/g, '');
        return textContent.length > 200 
            ? textContent.substring(0, 200) + '...'
            : textContent;
    }

    // Format date to match existing format
    formatDate(dateString) {
        const date = new Date(dateString);
        const options = { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        };
        return date.toLocaleDateString('en-US', options);
    }

    // Auto-categorize based on content keywords
    categorizeContent(content) {
        const contentLower = content.toLowerCase();
        
        if (contentLower.includes('ai') || contentLower.includes('machine learning') || contentLower.includes('artificial intelligence')) {
            return 'AI & Machine Learning';
        }
        if (contentLower.includes('gis') || contentLower.includes('geospatial') || contentLower.includes('mapping')) {
            return 'GIS & Geospatial Intelligence';
        }
        if (contentLower.includes('mobile') || contentLower.includes('app development') || contentLower.includes('flutter')) {
            return 'Mobile App Development';
        }
        if (contentLower.includes('full stack') || contentLower.includes('web development') || contentLower.includes('react')) {
            return 'Full Stack Development';
        }
        if (contentLower.includes('biomedical') || contentLower.includes('healthcare') || contentLower.includes('medical')) {
            return 'Biomedical Engineering';
        }
        
        return 'Technology'; // Default category
    }

    // Calculate read time based on content length
    calculateReadTime(content) {
        const wordsPerMinute = 200;
        const textContent = content.replace(/<[^>]*>/g, '');
        const wordCount = textContent.split(/\s+/).length;
        const minutes = Math.ceil(wordCount / wordsPerMinute);
        return `${minutes} min read`;
    }

    // Health check with connection warmup - try both endpoints
    async healthCheck() {
        // ULTRA AGGRESSIVE pre-rendering detection - IMMEDIATE RETURN
        if (IMMEDIATE_PRERENDER_CHECK || 
            IS_PRERENDERING || 
            buildDetection.shouldSkipApiCalls() || 
            (typeof window !== 'undefined' && window.__IS_BUILD_TIME__) ||
            (typeof window !== 'undefined' && window.__BLOG_API_DISABLED__)) {
            console.log('🔄 Pre-rendering detected in healthCheck (ultra aggressive check), skipping health check');
            return false;
        }

        const healthUrls = [
            `${LOCAL_API_URL}/health`,
            `${AZURE_API_URL}/health`
        ];
        
        for (const url of healthUrls) {
            // Try multiple times to warm up the connection
            for (let attempt = 1; attempt <= 2; attempt++) {
                try {
                    console.log(`Health check attempt ${attempt}: ${url}`);
                    
                    const controller = new AbortController();
                    const timeoutId = setTimeout(() => controller.abort(), 8000); // 8 second timeout
                    
                    const response = await fetch(url, {
                        signal: controller.signal,
                        headers: {
                            'Cache-Control': 'no-cache'
                        }
                    });
                    
                    clearTimeout(timeoutId);
                    
                    if (response.ok) {
                        console.log(`✅ Health check passed: ${url} (attempt ${attempt})`);
                        return true;
                    } else {
                        console.log(`❌ Health check failed: ${url} - HTTP ${response.status} (attempt ${attempt})`);
                        if (attempt === 1) {
                            // Wait before retry to allow connection warmup
                            await new Promise(resolve => setTimeout(resolve, 2000));
                        }
                    }
                } catch (error) {
                    console.log(`❌ Health check failed: ${url} (attempt ${attempt}):`, error.message);
                    if (attempt === 1) {
                        // Wait before retry to allow connection warmup
                        await new Promise(resolve => setTimeout(resolve, 2000));
                    }
                }
            }
        }
        
        console.log('📝 All health checks failed, API not available');
        return false;
    }

    // Create new blog
    async createBlog(blogData) {
        try {
            const response = await this.fetchFromMultipleEndpoints('/blogs');
            const data = await fetch(`${response.url}/blogs`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(blogData)
            });
            
            if (data.ok) {
                const result = await data.json();
                return result.success ? result.blog : null;
            }
            return null;
        } catch (error) {
            console.log('❌ Failed to create blog:', error);
            return null;
        }
    }

    // Update existing blog
    async updateBlog(id, blogData) {
        try {
            const response = await this.fetchFromMultipleEndpoints(`/blogs/${id}`);
            const data = await fetch(`${response.url}/blogs/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(blogData)
            });
            
            if (data.ok) {
                const result = await data.json();
                return result.success ? result.blog : null;
            }
            return null;
        } catch (error) {
            console.log('❌ Failed to update blog:', error);
            return null;
        }
    }
}

// eslint-disable-next-line import/no-anonymous-default-export
export default new BlogService();